/**
 * Schedule a report: a name, a cadence, and the addresses it goes to. Lists
 * the report's existing schedules so a person can pause or remove one here,
 * not in a settings page they would have to find.
 */

import { useCallback, useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Field, Modal, Select, Switch, TextArea, TextInput } from '../../../ds';
import { btnDanger, btnPrimary, btnSecondary } from '../../../mono/primitives';
import { useUIStore } from '../../../../stores/prioritization/uiStore';
import {
  CADENCE_OPTIONS,
  createReportSchedule,
  deleteReportSchedule,
  listReportSchedules,
  parseRecipients,
  setReportScheduleEnabled,
  type ReportSchedule,
  type ScheduleCadence,
} from '../../../../lib/reportSchedulesService';
import type { ReportSnapshotConfig } from '../../../../lib/reportSnapshotsService';

export interface ScheduleReportDialogProps {
  open: boolean;
  onClose: () => void;
  reportKey: string;
  reportName: string;
  config: ReportSnapshotConfig;
  /** The saved report is kept to the person viewing it, so readers get the numbers but not the links. */
  justMe?: boolean;
}

function describe(s: ReportSchedule): string {
  const cadence = CADENCE_OPTIONS.find((c) => c.value === s.cadence)?.label.toLowerCase() ?? s.cadence;
  const scope = s.config.segmentLabel ? `${s.config.rangeLabel} · by ${s.config.segmentLabel}` : s.config.rangeLabel;
  return `${cadence} · ${scope} · ${s.recipients.length} address${s.recipients.length === 1 ? '' : 'es'}`;
}

export function ScheduleReportDialog({ open, onClose, reportKey, reportName, config, justMe = false }: ScheduleReportDialogProps) {
  const addNotification = useUIStore((s) => s.addNotification);
  const [name, setName] = useState('');
  const [cadence, setCadence] = useState<ScheduleCadence>('weekly');
  const [recipientsText, setRecipientsText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existing, setExisting] = useState<ReportSchedule[]>([]);

  const reload = useCallback(async () => {
    setExisting(await listReportSchedules(reportKey));
  }, [reportKey]);

  useEffect(() => {
    if (!open) return;
    setName(`${reportName} digest`);
    setError(null);
    void reload();
  }, [open, reportName, reload]);

  const handleSave = useCallback(async () => {
    const { valid, invalid } = parseRecipients(recipientsText);
    if (invalid.length > 0) { setError(`Not an address: ${invalid.join(', ')}`); return; }
    if (valid.length === 0) { setError('Add at least one address.'); return; }
    if (!name.trim()) { setError('Give the schedule a name.'); return; }
    setSaving(true);
    const { schedule, error: saveError } = await createReportSchedule({ reportKey, name: name.trim(), config, cadence, recipients: valid });
    setSaving(false);
    if (!schedule) { setError(saveError ?? 'The schedule was not saved.'); return; }
    setRecipientsText('');
    setError(null);
    addNotification({ type: 'success', title: 'Scheduled', message: `"${schedule.name}" goes out ${CADENCE_OPTIONS.find((c) => c.value === cadence)?.label.toLowerCase()} to ${valid.length} address${valid.length === 1 ? '' : 'es'}.`, duration: 4000 });
    void reload();
  }, [recipientsText, name, reportKey, config, cadence, addNotification, reload]);

  const handleToggle = useCallback(async (s: ReportSchedule, enabled: boolean) => {
    const { error: e } = await setReportScheduleEnabled(s.id, enabled);
    if (e) addNotification({ type: 'error', title: 'Not changed', message: e, duration: 5000 });
    void reload();
  }, [addNotification, reload]);

  const handleDelete = useCallback(async (s: ReportSchedule) => {
    const { error: e } = await deleteReportSchedule(s.id);
    if (e) addNotification({ type: 'error', title: 'Not removed', message: e, duration: 5000 });
    void reload();
  }, [addNotification, reload]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      label="Scheduled digest"
      title={`Send ${reportName} on a schedule`}
      description={`A summary of this report's numbers, with a link to the live report and its print view, sent to the addresses below. ${justMe ? 'This report is just yours, so only you can open the links.' : 'Readers sign in to open the links.'}`}
      size="md"
      footer={
        <>
          <button type="button" {...btnSecondary()} onClick={onClose}>Close</button>
          <button type="button" {...btnPrimary()} onClick={() => void handleSave()} disabled={saving}>
            {saving ? 'Saving…' : 'Schedule'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Name" htmlFor="schedule-name">
          <TextInput id="schedule-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Monday stand-up" />
        </Field>
        <Field label="How often" htmlFor="schedule-cadence">
          <Select id="schedule-cadence" value={cadence} onChange={(e) => setCadence(e.target.value as ScheduleCadence)} options={CADENCE_OPTIONS.map((c) => ({ value: c.value, label: c.label }))} />
        </Field>
        <Field label="Send to" hint="Addresses, separated by commas or new lines." htmlFor="schedule-recipients" error={error}>
          <TextArea id="schedule-recipients" rows={3} value={recipientsText} onChange={(e) => setRecipientsText(e.target.value)} placeholder="leader@company.com, team@company.com" />
        </Field>
        <p className="text-xs text-[var(--text-ter)]">
          Sent with: {config.segmentLabel ? `${config.rangeLabel} · by ${config.segmentLabel}` : config.rangeLabel}.
        </p>

        {existing.length > 0 && (
          <div className="border-t border-[var(--rule)] pt-4">
            <p className="mb-2 font-mono text-[0.68rem] uppercase tracking-[0.12em] text-[var(--text-ter)]">Existing schedules</p>
            <ul className="space-y-2">
              {existing.map((s) => (
                <li key={s.id} className="flex items-center gap-3">
                  <Switch checked={s.enabled} onChange={(checked) => void handleToggle(s, checked)} label={s.name} description={describe(s)} />
                  <span className="ml-auto flex items-center gap-2">
                    {s.lastStatus && <span className="font-mono text-[0.65rem] uppercase tracking-[0.08em] text-[var(--text-ter)]" title={s.lastError ?? undefined}>{s.lastStatus}</span>}
                    <button type="button" {...btnDanger('h-7 px-2')} onClick={() => void handleDelete(s)} title="Remove this schedule">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
}
